import { useMemo, useState } from "react"
import { ChartTimeframeControls } from "@/components/chart/ChartTimeframeControls"
import { Swatch } from "@/components/chart/ChartTooltip"
import { SeriesChartSurface } from "@/components/chart/SeriesChartSurface"
import { captureEvent } from "@/lib/analytics"
import { cn } from "@/lib/cn"
import { useChartTheme } from "@/lib/chart/ChartThemeContext"
import { formatNumber, withUnit } from "@/lib/chart/format"
import { DEFAULT_CHART_RANGE, isCalendarTimeline, type ChartRange } from "@/lib/chart/timeframes"
import type { SeriesChartData } from "@/types/seriesChart"

interface Props {
  data: SeriesChartData
}

export function SeriesChart({ data }: Props) {
  const theme = useChartTheme()
  const defaultKeys = useMemo(() => data.series.map((item) => item.key), [data.series])
  const [visibleKeys, setVisibleKeys] = useState<string[]>(defaultKeys)
  const [knownKeys, setKnownKeys] = useState<string[]>(defaultKeys)
  if (knownKeys !== defaultKeys) {
    // New data: show newly added series, drop removed ones, keep the user's hidden ones hidden.
    const hidden = knownKeys.filter((key) => !visibleKeys.includes(key))
    const next = defaultKeys.filter((key) => !hidden.includes(key))
    setKnownKeys(defaultKeys)
    setVisibleKeys(next.length > 0 ? next : defaultKeys)
  }
  const [range, setRange] = useState<ChartRange>(DEFAULT_CHART_RANGE)
  const calendarTimeline = useMemo(
    () => isCalendarTimeline(data.series.flatMap((item) => item.points.map(([time]) => time))),
    [data.series],
  )
  const showRanges = calendarTimeline && data.ranges !== false

  if (data.series.length === 0 || data.series.every((item) => item.points.length === 0)) {
    return (
      <section className={cn("rounded-2xl border px-6 py-12 text-center", theme.classes.card, theme.classes.muted)}>
        {data.title} history is not available yet.
      </section>
    )
  }

  function toggleKey(key: string): void {
    setVisibleKeys((current) => {
      if (current.includes(key)) {
        if (current.length === 1) return current
        captureEvent("series_chart_series_toggled", { title: data.title, key, visible: false })
        return current.filter((item) => item !== key)
      }
      captureEvent("series_chart_series_toggled", { title: data.title, key, visible: true })
      return [...current, key]
    })
  }

  function handleRangeChange(next: ChartRange): void {
    setRange(next)
    captureEvent("series_chart_range_changed", { title: data.title, range: next, unit: data.unit })
  }

  const latestValues = data.series
    .filter((item) => visibleKeys.includes(item.key))
    .map((item) => {
      const last = [...item.points].reverse().find((point) => point[1] != null)
      const unit = item.pane === "lower" ? data.lower_unit : data.unit
      return { key: item.key, label: item.label, color: item.color, value: last?.[1] ?? null, unit }
    })

  return (
    <section className={cn("overflow-hidden rounded-2xl border", theme.classes.card)}>
      <div className={cn("border-b px-4 py-4 sm:px-5", theme.classes.header)}>
        <div className="flex flex-wrap items-center gap-3">
          <div className="min-w-0">
            <p className={cn("text-[10px] font-semibold uppercase tracking-[0.2em]", theme.classes.eyebrow)}>Market series</p>
            <div className="mt-1 flex flex-wrap items-baseline gap-3">
              <h2 className={cn("truncate text-lg font-semibold", theme.classes.title)}>{data.title}</h2>
              {data.unit && <span className={cn("text-xs", theme.classes.muted)}>{data.unit}</span>}
            </div>
            {data.subtitle && <p className={cn("mt-1 text-xs", theme.classes.muted)}>{data.subtitle}</p>}
          </div>
          <div className={cn("ml-auto flex flex-wrap gap-x-3 gap-y-1 font-mono text-[11px]", theme.classes.value)}>
            {latestValues.map((item) => (
              <span key={item.key} className="inline-flex items-center gap-1.5">
                <Swatch color={item.color} />
                {item.label} {item.value == null ? "—" : withUnit(formatNumber(item.value, { compact: false }), item.unit)}
              </span>
            ))}
          </div>
        </div>

        {(showRanges || data.series.length > 1) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {showRanges && <ChartTimeframeControls range={range} onRangeChange={handleRangeChange} />}
            {data.series.length > 1 && data.series.map((item) => {
              const visible = visibleKeys.includes(item.key)
              return (
                <button
                  key={item.key}
                  type="button"
                  aria-pressed={visible}
                  onClick={() => toggleKey(item.key)}
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-[11px] transition",
                    visible ? theme.classes.toggleOn : theme.classes.toggleOff,
                  )}
                >
                  <Swatch color={visible ? item.color : "currentColor"} />
                  {item.label}
                </button>
              )
            })}
          </div>
        )}
      </div>
      <SeriesChartSurface data={data} visibleKeys={visibleKeys} range={showRanges ? range : "all"} />
    </section>
  )
}
